import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import DerivativeList from "./DerivativeList";
import { useLanguage } from "@/contexts/LanguageContext";
import { DerivativeItem } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";

const DerivativeManagement = () => {
  const [derivatives, setDerivatives] = useState<DerivativeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [activeTab, setActiveTab] = useState("approved");
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    fetchDerivatives();
  }, [user]);
  
  const fetchDerivatives = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setIsError(false);
      
      console.log("Fetching derivatives for user:", user.id);
      
      const { data: derivativeData, error } = await supabase
        .from('derivatives')
        .select(`
          *,
          playlists:playlist_id (
            id, 
            name
          )
        `)
        .eq('user_id', user.id);
      
      if (error) {
        console.error("Error fetching derivatives:", error);
        toast.error("An error occurred while loading your derivatives");
        setIsError(true);
        return;
      }
      
      console.log("Raw derivatives data:", derivativeData);
      
      const formattedData: DerivativeItem[] = derivativeData.map(item => {
        console.log(`Processing derivative ${item.id}, social_links:`, item.social_links);
        
        let processedClips;
        
        if (item.original_clips && Array.isArray(item.original_clips)) {
          processedClips = item.original_clips.map((clip: any) => {
            return {
              clip_id: clip.clip_id || clip.id || "",
              title: clip.title || ""
            };
          });
        } else {
          processedClips = [];
        }
        
        // Process social_links
        let socialLinks = null;
        if (item.social_links) {
          try {
            if (typeof item.social_links === 'string') {
              console.log(`Derivative ${item.id} has string social_links, parsing:`, item.social_links);
              socialLinks = JSON.parse(item.social_links);
            } else {
              console.log(`Derivative ${item.id} has object social_links:`, item.social_links);
              socialLinks = item.social_links;
            }
          } catch (e) {
            console.error(`Error parsing social_links for derivative ${item.id}:`, e);
            socialLinks = null;
          }
        }
        
        console.log(`Final social_links for derivative ${item.id}:`, socialLinks);
        
        return {
          id: item.id,
          title: item.title,
          originalClipId: item.original_clip_id || "",
          originalTitle: item.original_title || "Unknown",
          originalCreator: item.original_creator || "Unknown Creator",
          submittedDate: new Date(item.submitted_date).toLocaleDateString(),
          approvedDate: item.approved_date ? new Date(item.approved_date).toLocaleDateString() : undefined,
          rejectedDate: item.rejected_date ? new Date(item.rejected_date).toLocaleDateString() : undefined,
          thumbnailUrl: item.thumbnail_url,
          description: item.description || "",
          videoUrl: item.video_url,
          expressionType: item.expression_type,
          status: (item.status as "pending" | "approved" | "rejected") || "pending",
          reviewComment: item.review_comment || "",
          aspectRatio: item.aspect_ratio || 16/9,
          originalClips: processedClips,
          playlistId: item.playlist_id || "",
          playlistName: item.playlists ? item.playlists.name : "",
          social_links: socialLinks
        };
      });
      
      console.log("Formatted derivatives data:", formattedData);
      setDerivatives(formattedData);
    } catch (error) {
      console.error("Unexpected error fetching derivatives:", error);
      toast.error("An unexpected error occurred while loading your derivatives");
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEditDerivative = (id: string) => {
    navigate(`/edit-derivative/${id}`);
  };
  
  const handleViewDerivative = (id: string) => {
    navigate(`/derivative/${id}`);
  };
  
  const handleDeleteDerivative = async (id: string) => {
    try {
      const { error } = await supabase
        .from('derivatives')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast.error(t('errors.generalError'));
        return;
      }
      
      setDerivatives(derivatives.filter(d => d.id !== id));
      toast.success(t('derivatives.deleted'), {
        description: t('creator.deleteSuccess'),
      });
    } catch (error) {
      toast.error(t('errors.unexpectedError'));
    }
  };
  
  const handleRefresh = () => {
    fetchDerivatives();
  };

  const approvedDerivatives = derivatives.filter(d => d.status === "approved");
  const pendingDerivatives = derivatives.filter(d => d.status === "pending");
  const rejectedDerivatives = derivatives.filter(d => d.status === "rejected");

  const getCountBadge = (count: number) => {
    return <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium">{count}</span>;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'justify-between items-center'}`}>
        <div>
          <h2 className={`font-semibold ${isMobile ? 'text-xl' : 'text-2xl'}`}>{t('creator.yourCreations')}</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">{t('creator.manageCreations')}</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto justify-between md:justify-end">
          <Button variant="outline" size={isMobile ? "sm" : "default"} onClick={handleRefresh} disabled={isLoading} className="flex-1 md:flex-none">
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            {t('creator.refresh')}
          </Button>
          <Button onClick={() => navigate("/create-derivative")} className="flex-1 md:flex-none" size={isMobile ? "sm" : "default"}>
            <Plus className="h-4 w-4 mr-1" />
            {isMobile ? t('creator.createNew') : t('derivatives.createNew')}
          </Button>
        </div>
      </div>
      
      <div className="bg-card rounded-lg border p-1">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="approved" className="text-sm py-2 px-3 h-12">
              {t('derivatives.status.approved')} {getCountBadge(approvedDerivatives.length)}
            </TabsTrigger>
            <TabsTrigger value="pending" className="text-sm py-2 px-3 h-12">
              {t('derivatives.status.pending')} {getCountBadge(pendingDerivatives.length)}
            </TabsTrigger>
            <TabsTrigger value="rejected" className="text-sm py-2 px-3 h-12">
              {t('derivatives.status.rejected')} {getCountBadge(rejectedDerivatives.length)}
            </TabsTrigger>
          </TabsList>
          
          <div className="relative min-h-[300px]">
            {isLoading ? (
              <div className="absolute inset-0 flex justify-center items-center">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
                </div>
              </div>
            ) : (
              <>
                <TabsContent value="approved" className="mt-0 pb-2 px-1 md:px-2">
                  <DerivativeList 
                    derivatives={approvedDerivatives} 
                    onEdit={handleEditDerivative} 
                    onDelete={handleDeleteDerivative}
                    onView={handleViewDerivative}
                    showEditButton={true}
                    displayAsList={true}
                  />
                </TabsContent>
                
                <TabsContent value="pending" className="mt-0 pb-2 px-1 md:px-2">
                  <DerivativeList 
                    derivatives={pendingDerivatives}
                    onEdit={handleEditDerivative} 
                    onDelete={handleDeleteDerivative}
                    onView={handleViewDerivative}
                    showEditButton={true}
                    displayAsList={true}
                  />
                </TabsContent>
                
                <TabsContent value="rejected" className="mt-0 pb-2 px-1 md:px-2">
                  <DerivativeList 
                    derivatives={rejectedDerivatives}
                    onEdit={handleEditDerivative} 
                    onDelete={handleDeleteDerivative}
                    onView={handleViewDerivative}
                    showEditButton={true}
                    displayAsList={true}
                  />
                </TabsContent>
              </>
            )}
            
            {isError && !isLoading && (
              <div className="flex flex-col justify-center items-center py-8 space-y-4">
                <p className="text-red-500">{t('errors.failedToLoad')}</p>
                <Button onClick={handleRefresh} size="sm">{t('common.retry')}</Button>
              </div>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default DerivativeManagement;
